import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api';
import Modal from '../../components/Modal';

const EMPTY_FORM = {
  name: '',
  description: '',
  interval_miles: '',
  interval_days: '',
  due_soon_miles: 500,
  due_soon_days: 7,
};

function formatInterval(value, unit) {
  if (!value) return '\u2014';
  return unit === 'mi'
    ? `${Number(value).toLocaleString()} mi`
    : `${Number(value)} days`;
}

export default function MaintenanceTypesPage() {
  const { can } = useAuth();
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const loadTypes = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getMaintenanceTypes();
      setTypes(Array.isArray(data) ? data : (data.types || []));
    } catch (err) {
      setError(err.message || 'Failed to load maintenance types');
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadTypes(); }, [loadTypes]);

  function openAdd() {
    setEditingType(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
  }

  function openEdit(type) {
    setEditingType(type);
    setForm({
      name: type.name || '',
      description: type.description || '',
      interval_miles: type.interval_miles || '',
      interval_days: type.interval_days || '',
      due_soon_miles: type.due_soon_miles ?? 500,
      due_soon_days: type.due_soon_days ?? 7,
    });
    setShowModal(true);
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim(),
        interval_miles: form.interval_miles ? Number(form.interval_miles) : null,
        interval_days: form.interval_days ? Number(form.interval_days) : null,
        due_soon_miles: form.due_soon_miles ? Number(form.due_soon_miles) : 500,
        due_soon_days: form.due_soon_days ? Number(form.due_soon_days) : 7,
      };
      if (editingType) {
        await api.updateMaintenanceType(editingType.id, payload);
      } else {
        await api.createMaintenanceType(payload);
      }
      setShowModal(false);
      loadTypes();
    } catch (err) {
      setError(err.message || 'Failed to save maintenance type');
    }
    setSaving(false);
  }

  async function handleDelete(id) {
    try {
      await api.deleteMaintenanceType(id);
      setDeleteConfirm(null);
      loadTypes();
    } catch (err) {
      setError(err.message || 'Failed to delete maintenance type');
      setDeleteConfirm(null);
    }
  }

  function handleChange(e) {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  }

  if (!can('manage_maintenance')) {
    return <div className="page-content"><p>You do not have permission to manage maintenance types.</p></div>;
  }

  return (
    <div className="maint-types">
      <div className="maint-types__header">
        <h1>Maintenance Types</h1>
        <button className="btn btn-gold" onClick={openAdd}>+ Add Type</button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {loading ? (
        <div className="loading-spinner">Loading maintenance types...</div>
      ) : (
        <div className="table-responsive">
          <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Description</th>
                <th>Mile Interval</th>
                <th>Day Interval</th>
                <th>Due Soon (Miles)</th>
                <th>Due Soon (Days)</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {types.length === 0 ? (
                <tr><td colSpan="7" className="text-center">No maintenance types found. Add one to get started.</td></tr>
              ) : types.map(type => (
                <tr key={type.id}>
                  <td><strong>{type.name}</strong></td>
                  <td>{type.description || '\u2014'}</td>
                  <td>{formatInterval(type.interval_miles, 'mi')}</td>
                  <td>{formatInterval(type.interval_days, 'days')}</td>
                  <td>{formatInterval(type.due_soon_miles, 'mi')}</td>
                  <td>{formatInterval(type.due_soon_days, 'days')}</td>
                  <td>
                    <button className="btn btn-sm btn-outline" onClick={() => openEdit(type)}>Edit</button>
                    {' '}
                    <button className="btn btn-sm btn-danger" onClick={() => setDeleteConfirm(type)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <Modal title={editingType ? 'Edit Maintenance Type' : 'Add Maintenance Type'} onClose={() => setShowModal(false)}>
          <form onSubmit={handleSave}>
            <div className="form-grid">
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label>Name *</label>
                <input name="name" value={form.name} onChange={handleChange} required className="form-input" placeholder="e.g. Oil Change" />
              </div>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label>Description</label>
                <textarea name="description" value={form.description} onChange={handleChange} className="form-input" rows={3} placeholder="What this maintenance covers..." />
              </div>
              <div className="form-group">
                <label>Mile Interval</label>
                <input name="interval_miles" type="number" min="0" value={form.interval_miles} onChange={handleChange} className="form-input" placeholder="e.g. 5000" />
              </div>
              <div className="form-group">
                <label>Day Interval</label>
                <input name="interval_days" type="number" min="0" value={form.interval_days} onChange={handleChange} className="form-input" placeholder="e.g. 90" />
              </div>
              <div className="form-group">
                <label>Due Soon Miles</label>
                <input name="due_soon_miles" type="number" min="0" value={form.due_soon_miles} onChange={handleChange} className="form-input" placeholder="500" />
              </div>
              <div className="form-group">
                <label>Due Soon Days</label>
                <input name="due_soon_days" type="number" min="0" value={form.due_soon_days} onChange={handleChange} className="form-input" placeholder="7" />
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
              <button type="submit" className="btn btn-gold" disabled={saving}>
                {saving ? 'Saving...' : (editingType ? 'Update Type' : 'Add Type')}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <Modal title="Delete Maintenance Type" onClose={() => setDeleteConfirm(null)}>
          <p>Are you sure you want to delete <strong>{deleteConfirm.name}</strong>? This will remove all associated maintenance schedules. This action cannot be undone.</p>
          <div className="modal-actions">
            <button className="btn btn-outline" onClick={() => setDeleteConfirm(null)}>Cancel</button>
            <button className="btn btn-danger" onClick={() => handleDelete(deleteConfirm.id)}>Delete</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
